import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCheckCircle, 
  faInfoCircle, 
  faSpinner, 
  faPlus,
  faPhone,
  faEnvelope,
  faCalendarAlt,
  faComment,
  faFileAlt,
  faHandshake
} from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import './AddActivityPage.css';

interface AddActivityPageProps {
  onClose: () => void;
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
  onActivityAdded?: () => void;
}

interface ActivityType {
  id: string;
  label: string;
  icon: any;
  color: string;
}

const ACTIVITY_TYPES: ActivityType[] = [
  { id: 'meeting', label: 'Meeting', icon: faHandshake, color: '#0ea5e9' },
  { id: 'call', label: 'Call', icon: faPhone, color: '#10b981' },
  { id: 'email', label: 'Email', icon: faEnvelope, color: '#8b5cf6' },
  { id: 'note', label: 'Note', icon: faComment, color: '#f59e0b' },
  { id: 'document', label: 'Document', icon: faFileAlt, color: '#ef4444' },
  { id: 'task', label: 'Task Completed', icon: faCheckCircle, color: '#06b6d4' },
];

const AddActivityPage: React.FC<AddActivityPageProps> = ({ 
  onClose, 
  stakeholderId, 
  type,
  stakeholderData,
  onActivityAdded,
}) => {
  const [activityText, setActivityText] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('meeting');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const employeeId = useActiveEmployeeId();

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setActivityText(e.target.value);
  };

  const handleClose = () => {
    setActivityText('');
    setSelectedType('meeting');
    setError('');
    onClose();
  };

  // A17: close on Escape (parity with the shared FormModal; this custom overlay had no keyboard dismiss).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleActivityCreation = async () => {
    setLoading(true);
    setError('');

    if (!activityText.trim()) {
      setLoading(false);
      setError('Please enter activity details.');
      return;
    }

    if (!employeeId) {
      setLoading(false);
      setError('Unable to identify user. Please refresh the page and try again.');
      return;
    }

    try {
      // Resolve client vs agent: try client first, then agent
      let clientData: any = null;
      let agentData: any = null;
      try {
        clientData = await ClientService.getClientById(stakeholderId);
      } catch {
        clientData = null;
      }
      if (!clientData) {
        try {
          agentData = await AgentsService.getAgent(stakeholderId);
        } catch {
          agentData = null;
        }
      }

      if (clientData) {
        // --- Client path: use recent_activity on client ---
        let existingActivities: any[] = [];
        const activitiesField = (clientData as any).recent_activity || (clientData as any).RecentActivity;

        if (activitiesField) {
          try {
            const activitiesData = typeof activitiesField === 'string' ? JSON.parse(activitiesField) : activitiesField;
            if (Array.isArray(activitiesData)) {
              existingActivities = activitiesData;
            } else if (typeof activitiesData === 'object' && activitiesData !== null) {
              existingActivities = [activitiesData];
            }
          } catch (error) {
            console.error('Error parsing existing activities:', error);
            existingActivities = [];
          }
        }

        const newActivity = {
          activity_id: existingActivities.length + 1,
          activity_type: selectedType,
          activity_text: activityText.trim(),
          created_at: new Date().toISOString(),
          created_by: employeeId,
        };
        existingActivities.unshift(newActivity);

        const getFieldValue = (field: string, isDateField: boolean = false) => {
          const snakeValue = (clientData as any)[field];
          const pascalKey = field.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
          const capitalizedKey = field.charAt(0).toUpperCase() + field.slice(1);
          const pascalValue = (clientData as any)[pascalKey];
          const capitalizedValue = (clientData as any)[capitalizedKey];
          const value = snakeValue !== undefined && snakeValue !== null
            ? snakeValue
            : pascalValue !== undefined && pascalValue !== null
            ? pascalValue
            : capitalizedValue;

          if (isDateField) {
            if (value === '' || value === null || value === undefined) {
              return null;
            }
          }
          return value !== null && value !== undefined ? value : '';
        };

        const updateData: any = {
          FirstName: getFieldValue('first_name'),
          LastName: getFieldValue('last_name'),
          Company: getFieldValue('company'),
          Email: getFieldValue('email'),
          Phone: getFieldValue('phone'),
          Address: getFieldValue('address'),
          Type: getFieldValue('type'),
          ContactName: getFieldValue('contact_name'),
          Prefix: getFieldValue('prefix'),
          MiddleName: getFieldValue('middle_name'),
          DateOfBirth: getFieldValue('date_of_birth', true),
          SecondaryPhoneNumber: getFieldValue('secondary_phone_number'),
          Entity: getFieldValue('entity'),
          Title: getFieldValue('title'),
          ContactCreatedDate: getFieldValue('contact_created_date', true),
          PrimaryAddressCity: getFieldValue('primary_address_city'),
          PrimaryAddressProvince: getFieldValue('primary_address_province'),
          PrimaryAddressPostalZip: getFieldValue('primary_address_postal_zip'),
          PrimaryAddressCountry: getFieldValue('primary_address_country'),
          SecondaryEmailAddress: getFieldValue('secondary_email_address'),
          SecondaryAddressStreet: getFieldValue('secondary_address_street'),
          SecondaryAddressCity: getFieldValue('secondary_address_city'),
          SecondaryAddressProvinceState: getFieldValue('secondary_address_province_state'),
          SecondaryAddressPostalZip: getFieldValue('secondary_address_postal_zip'),
          SecondaryAddressCountry: getFieldValue('secondary_address_country'),
          FormalNameIfDifferent: getFieldValue('formal_name_if_different'),
          Notes: getFieldValue('notes'),
          RecentActivity: JSON.stringify(existingActivities),
          AssignedLead: getFieldValue('assigned_lead'),
        };

        await ClientService.updateClient(stakeholderId, updateData);
      } else if (agentData) {
        // --- Agent path: store activities inside Notes as { notes, recent_activity } ---
        const notesField = (agentData as any).Notes ?? (agentData as any).notes;
        let notesArray: any[] = [];
        let existingActivities: any[] = [];

        if (notesField) {
          try {
            const parsed = typeof notesField === 'string' ? JSON.parse(notesField) : notesField;
            if (Array.isArray(parsed)) {
              notesArray = parsed;
              existingActivities = [];
            } else if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              notesArray = Array.isArray(parsed.notes) ? parsed.notes : [];
              existingActivities = Array.isArray(parsed.recent_activity) ? parsed.recent_activity : [];
            }
          } catch (e) {
            console.error('Error parsing agent notes:', e);
          }
        }

        const newActivity = {
          activity_id: existingActivities.length + 1,
          activity_type: selectedType,
          activity_text: activityText.trim(),
          created_at: new Date().toISOString(),
          created_by: employeeId,
        };
        existingActivities.unshift(newActivity);

        const getAgentField = (key: string) => {
          const v = (agentData as any)[key];
          return v !== null && v !== undefined ? v : '';
        };

        const updatePayload: any = {
          FirstName: getAgentField('FirstName'),
          LastName: getAgentField('LastName'),
          Company: getAgentField('Company'),
          Email: getAgentField('Email'),
          Phone: getAgentField('Phone'),
          Address: getAgentField('Address'),
          Jurisdiction: getAgentField('Jurisdiction'),
          SecondaryPhoneNumber: getAgentField('SecondaryPhoneNumber'),
          Entity: getAgentField('Entity'),
          Title: getAgentField('Title'),
          ContactCreatedAt: getAgentField('ContactCreatedAt'),
          PrimaryAddressCity: getAgentField('PrimaryAddressCity'),
          PrimaryAddressProvince: getAgentField('PrimaryAddressProvince'),
          PrimaryAddressPostal: getAgentField('PrimaryAddressPostal'),
          PrimaryAddressCountry: getAgentField('PrimaryAddressCountry'),
          SecondaryEmail: getAgentField('SecondaryEmail'),
          SecondaryAddressStreet: getAgentField('SecondaryAddressStreet'),
          SecondaryAddressCity: getAgentField('SecondaryAddressCity'),
          SecondaryAddressProvince: getAgentField('SecondaryAddressProvince'),
          SecondaryAddressPostal: getAgentField('SecondaryAddressPostal'),
          SecondaryAddressCountry: getAgentField('SecondaryAddressCountry'),
          ContactType: getAgentField('ContactType'),
          FormalName: getAgentField('FormalName'),
          Notes: JSON.stringify({ notes: notesArray, recent_activity: existingActivities }),
          ContactName: getAgentField('ContactName'),
          MiddleName: getAgentField('MiddleName'),
          DateOfBirth: getAgentField('DateOfBirth'),
        };

        await AgentsService.updateAgent(stakeholderId, updatePayload);
      } else {
        setError('Stakeholder not found.');
        setLoading(false);
        return;
      }

      toastService.success('Activity added successfully');

      if (onActivityAdded) {
        onActivityAdded();
      }

      handleClose();
    } catch (error: any) {
      console.error('Error adding activity:', error);
      setError(error.message || 'Failed to add the activity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedActivityType = ACTIVITY_TYPES.find(t => t.id === selectedType);

  return (
    <div className="add-activity-overlay" onClick={handleClose}>
      <div className="add-activity-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-activity-header">
          <div className="add-activity-header-left">
            <div className="add-activity-title-row">
              <div className="add-activity-header-icon">
                <FontAwesomeIcon icon={faPlus} />
              </div>
              <div className="add-activity-header-text">
                <h2 className="add-activity-title">Add Activity</h2>
                <p className="add-activity-subtitle">Log an activity for this {type === 'organization' ? 'organization' : 'stakeholder'}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="add-activity-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="add-activity-content">
          {/* Activity Type Section */}
          <div className="add-activity-section add-activity-section-blue">
            <div className="add-activity-section-header">
              <div className="add-activity-section-icon-wrapper add-activity-icon-blue">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <h3 className="add-activity-section-title">Activity Type</h3>
            </div>
            <div className="add-activity-section-content">
              <div className="add-activity-type-grid">
                {ACTIVITY_TYPES.map((activityType) => (
                  <button
                    key={activityType.id}
                    type="button"
                    className={`add-activity-type-btn ${selectedType === activityType.id ? 'add-activity-type-btn-active' : ''}`}
                    onClick={() => setSelectedType(activityType.id)}
                    style={{
                      '--activity-color': activityType.color,
                    } as React.CSSProperties}
                  >
                    <FontAwesomeIcon icon={activityType.icon} className="add-activity-type-icon" />
                    <span className="add-activity-type-label">{activityType.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Activity Details Section */}
          <div className="add-activity-section add-activity-section-green">
            <div className="add-activity-section-header">
              <div 
                className="add-activity-section-icon-wrapper"
                style={{ background: selectedActivityType?.color || '#0ea5e9' }}
              >
                <FontAwesomeIcon icon={selectedActivityType?.icon || faComment} />
              </div>
              <h3 className="add-activity-section-title">Activity Details</h3>
            </div>
            <div className="add-activity-section-content">
              <div className="add-activity-textarea-wrapper">
                <textarea
                  value={activityText}
                  onChange={handleTextChange}
                  placeholder={`Describe the ${selectedActivityType?.label.toLowerCase() || 'activity'}...`}
                  rows={6}
                  className="add-activity-textarea"
                  maxLength={1000}
                />
                <div className="add-activity-char-counter">
                  {activityText.length}/1000
                </div>
              </div>
            </div>
          </div>

          {error && <div className="add-activity-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="add-activity-footer">
          <div className="add-activity-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="add-activity-footer-info-icon" />
            <span>Activity will be logged and visible to your team</span>
          </div>
          <div className="add-activity-footer-buttons">
            <button
              type="button"
              className="add-activity-button add-activity-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="add-activity-button add-activity-button-primary"
              onClick={handleActivityCreation}
              disabled={loading || !activityText.trim()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Adding...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Add Activity
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddActivityPage;
